import React, { createContext, useEffect, useReducer } from "react";
import {
  ExpressStoreKey,
  getExpressSectionDataByKey,
  putExpressSectionDataByKey,
} from "@/pages/ExpressForm/express-data";
import { NotifierRolesByServer, Person, YesNo } from "@/types/FormData";
import { Document, FileTags } from "@/types/Document";
import { ServiceProvider } from "@/types/ServiceProvider";
import { Account } from "@/types/Account";
import { Attachment } from "@/types/Attachment";
import { Case } from "@/types/Case";
import { AdditionalHelpDetails, allAdditionalServices } from "@/types";
import { servicesFromCase } from "@/helpers/additional-services-helpers";
import { isExpressConvertedToNEForm } from "@/helpers/express-helpers";
import { showAdditionalServicesAccordion } from "@/helpers/dashboard-helpers";

export type DashboardInfo = {
  nok?: Person;
  executor?: Person;
  notifier?: Person;
  attachments?: Document[];
  serviceProviders: ServiceProvider[];
  intestacyFlow: boolean;
  collectNokDocuments: boolean;
  collectExecutorDocuments: boolean;
  nokIdDocUploaded: boolean;
  executorIdDocUploaded: boolean;
  nokGoaDocUploaded: boolean;
  executorGoaDocUploaded: boolean;
  caseId?: string;
  signature?: string;
  refreshCounter: number;
  case:
    | null
    | (Case & {
        accounts: Array<Account>;
        attachments?: Attachment[];
        notifier: Person & { attachments?: Attachment[] };
      });
  showInheritNowAccordion: boolean;
  isExpressConvertedToNEForm: boolean;
  showAdditionalServicesAccordion: boolean;
  accounts: ReadonlyArray<Account>;
  services: AdditionalHelpDetails;
};

const initialState: DashboardInfo = {
  serviceProviders: [],
  intestacyFlow: false,
  collectNokDocuments: false,
  collectExecutorDocuments: false,
  nokIdDocUploaded: false,
  executorIdDocUploaded: false,
  nokGoaDocUploaded: false,
  executorGoaDocUploaded: false,
  refreshCounter: 0,
  case: null,
  showInheritNowAccordion: true,
  isExpressConvertedToNEForm: false,
  showAdditionalServicesAccordion: false,
  accounts: [],
  services: {} as AdditionalHelpDetails,
};

const DashboardInfoContext = createContext<{
  state: DashboardInfo;
  dispatch: React.Dispatch<any>;
}>({ state: initialState, dispatch: () => undefined });

const actionTypes = {
  SET: "SET",
  SET_ALL: "SET_ALL",
  REFRESH: "REFRESH",
};
const INHERIT_NOW_KEY = "requestInheritanceFundingFinance";

function reducer(state: DashboardInfo, action: { type: string; payload: any }): DashboardInfo {
  switch (action.type) {
    case actionTypes.SET:
      return { ...state, ...action.payload };
    case actionTypes.REFRESH:
      return { ...state, refreshCounter: state.refreshCounter + 1 };
    case actionTypes.SET_ALL: {
      const { persons, caseId, signature, case: caseRecord } = action.payload;
      const accounts = caseRecord.accounts.sort((a: Account, b: Account) => {
        const aCompanyName = a.serviceProvider?.companyName ?? "";
        const bCompanyName = b.serviceProvider?.companyName ?? "";
        const s = aCompanyName.localeCompare(bCompanyName);
        return s === 0 ? (a.insertedAt || "").localeCompare(b.insertedAt ?? "") : s;
      });
      const attachments = caseRecord.attachments ?? [];
      const serviceProviders = accounts.map((account: Account) => account.serviceProvider);
      const executor = persons.find((person: any) =>
        person.roles.includes(NotifierRolesByServer.Executor)
      );
      const nok = persons.find((person: any) => person.roles.includes(NotifierRolesByServer.Nok));
      const notifier = persons.find((person: any) =>
        person.roles.includes(NotifierRolesByServer.Notifier)
      );
      const intestacyFlow = serviceProviders.some((sp: ServiceProvider) => sp.intestacyFlow);
      const nokIdDocUploaded = attachments.some(({ document: { tags } }: any) =>
        tags?.includes(FileTags.NokId)
      );
      const executorIdDocUploaded = attachments.some(({ document: { tags } }: any) =>
        tags?.includes(FileTags.ExecutorId)
      );
      const nokGoaDocUploaded = attachments.some(({ document: { tags } }: any) =>
        tags?.includes(FileTags.NokGoa)
      );
      const executorGoaDocUploaded = attachments.some(({ document: { tags } }: any) =>
        tags?.includes(FileTags.ExecutorGoa)
      );
      const showInheritNowAccordion =
        caseRecord[INHERIT_NOW_KEY] === YesNo.No || !caseRecord[INHERIT_NOW_KEY];

      return {
        ...state,
        collectNokDocuments: intestacyFlow && !!nok,
        nokIdDocUploaded: intestacyFlow && !!nok && nokIdDocUploaded,
        nokGoaDocUploaded: intestacyFlow && !!nok && nokGoaDocUploaded,
        collectExecutorDocuments: intestacyFlow && !!executor,
        executorIdDocUploaded: intestacyFlow && !!executor && executorIdDocUploaded,
        executorGoaDocUploaded: intestacyFlow && !!executor && executorGoaDocUploaded,
        intestacyFlow,
        serviceProviders,
        attachments,
        executor,
        nok,
        notifier,
        caseId,
        signature,
        case: caseRecord,
        showInheritNowAccordion,
        isExpressConvertedToNEForm: isExpressConvertedToNEForm(caseRecord),
        showAdditionalServicesAccordion: showAdditionalServicesAccordion(caseRecord),
        accounts,
        services: servicesFromCase(caseRecord),
      };
    }
    default:
      return state;
  }
}

const DashboardInfoProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const source = getExpressSectionDataByKey(ExpressStoreKey.DashboardInfo);
    if (source) dispatch({ type: actionTypes.SET, payload: source });
  }, []);

  useEffect(() => {
    putExpressSectionDataByKey(ExpressStoreKey.DashboardInfo, state);
  }, [state]);

  return (
    <DashboardInfoContext.Provider value={{ state, dispatch }}>
      {children}
    </DashboardInfoContext.Provider>
  );
};

export { DashboardInfoContext, DashboardInfoProvider };
